import { getFirstPhotoUrl, isDirectGooglePhotoUrl, type PhotoIdentifier } from '@/utils/photoUtils';
import { log, debug, warn } from '@/utils/logger';
import { getExplicitRecBaseFromLocationData } from '@/utils/recommendationCounts';

/**
 * Type for rec_count_breakdown from backend
 */
interface RecCountBreakdown {
  post_locations?: number;
  explicit_recommendations?: number;
}

/**
 * Type for rec_references array item
 */
interface RecReference {
  user_id?: string;
  user_name?: string;
  user_small_avatar_url?: string;
  user_large_avatar_url?: string;
}

/**
 * Avatar URLs from `location.rec_references` (snake_case and camelCase payloads).
 * Ignores entries with no usable URL so we can still fetch full location when the
 * array is non-empty but avatars were stripped from list/post embeddings.
 */
function pickFirstNonEmptyString(...candidates: unknown[]): string {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) return c.trim();
  }
  return '';
}

/** Key suggests a profile / avatar image field (not generic "image" / Google photo ids). */
const AVATARISH_KEY = /(avatar|profile|userpic|picture|headshot|portrait|photo_?url|gravatar)/i;

/**
 * Last-resort: walk a shallow tree for any https URL on avatar-ish keys (unknown backend shapes).
 */
function firstHttpUrlUnderAvatarLikeKeys(obj: Record<string, unknown>, depth: number): string {
  if (depth < 0) return '';
  for (const [key, val] of Object.entries(obj)) {
    if (typeof val === 'string') {
      const s = val.trim();
      if (s.length > 12 && /^https?:\/\//i.test(s) && AVATARISH_KEY.test(key)) return s;
    } else if (val && typeof val === 'object' && !Array.isArray(val)) {
      const nested = firstHttpUrlUnderAvatarLikeKeys(val as Record<string, unknown>, depth - 1);
      if (nested) return nested;
    }
  }
  return '';
}

/**
 * One recommender row from `rec_references` — backends differ (location DTO vs public recommender).
 */
function avatarUrlFromRecReferenceRow(ref: Record<string, unknown>): string {
  const small = pickFirstNonEmptyString(
    ref.user_small_avatar_url,
    ref.userSmallAvatarUrl,
    ref.small_avatar_url,
    ref.smallAvatarUrl,
  );
  const large = pickFirstNonEmptyString(
    ref.user_large_avatar_url,
    ref.userLargeAvatarUrl,
    ref.large_avatar_url,
    ref.largeAvatarUrl,
  );
  const direct = pickFirstNonEmptyString(
    ref.avatar_url,
    ref.avatarUrl,
    ref.profile_image_url,
    ref.profileImageUrl,
    ref.profile_picture_url,
    ref.profilePictureUrl,
    ref.profile_photo_url,
    ref.profilePhotoUrl,
    ref.photoURL,
    ref.photoUrl,
    ref.image_url,
    ref.imageUrl,
  );
  if (small || large || direct) return small || large || direct;

  const user = ref.user;
  if (user && typeof user === 'object') {
    const u = user as Record<string, unknown>;
    const fromUser = pickFirstNonEmptyString(
      u.user_small_avatar_url,
      u.userSmallAvatarUrl,
      u.small_avatar_url,
      u.smallAvatarUrl,
      u.user_large_avatar_url,
      u.userLargeAvatarUrl,
      u.large_avatar_url,
      u.largeAvatarUrl,
      u.profile_image_url,
      u.profileImageUrl,
      u.avatar_url,
      u.avatarUrl,
      u.profile_photo_url,
      u.profilePhotoUrl,
      u.photoURL,
      u.photoUrl,
      u.image_url,
      u.imageUrl,
    );
    if (fromUser) return fromUser;
  }
  return firstHttpUrlUnderAvatarLikeKeys(ref, 5);
}

/** Normalize location payload: some APIs use `recReferences` (camelCase). */
export function getRecReferencesArrayFromLocationLike(
  locationLike: Record<string, unknown> | null | undefined,
): unknown[] | undefined {
  if (!locationLike || typeof locationLike !== 'object') return undefined;
  const raw = locationLike.rec_references ?? locationLike.recReferences;
  return Array.isArray(raw) ? raw : undefined;
}

/**
 * Recommender avatar URLs from a location-like payload — **same extraction as
 * list and itinerary cards** (`convertTimelinePlaceToPlaceCardData`, `listViewDataConverter`).
 *
 * Order: `rec_references` / `recReferences` first (public recommender rows). If that yields
 * no URLs, falls back to `recommendations` (some APIs attach avatars only there, or only
 * under `recommendations[].user`). The place modal also merges `GET /location/:id` via
 * `fetchedRecAvatars` when the embedded payload has no URLs.
 */
export function getRecommendationAvatarUrlsFromLocationLike(locationLike: unknown): string[] {
  if (!locationLike || typeof locationLike !== 'object') return [];
  const obj = locationLike as Record<string, unknown>;

  const fromRefs = extractAvatarUrlsFromRecReferences(
    getRecReferencesArrayFromLocationLike(obj),
  );
  if (fromRefs.length > 0) return fromRefs;

  const recs = obj.recommendations;
  if (!Array.isArray(recs) || recs.length === 0) return [];
  return extractAvatarUrlsFromRecReferences(recs as unknown[]);
}

export function extractAvatarUrlsFromRecReferences(
  refs: unknown[] | undefined | null,
): string[] {
  if (!Array.isArray(refs) || refs.length === 0) return [];
  return refs
    .map((ref) => {
      if (!ref || typeof ref !== 'object') return '';
      return avatarUrlFromRecReferenceRow(ref as Record<string, unknown>);
    })
    .filter((url): url is string => url.length > 0);
}

export interface RecommenderAvatar {
  userId: string;
  avatarUrl: string;
}

function userIdFromRow(ref: Record<string, unknown>): string {
  const direct = pickFirstNonEmptyString(ref.user_id, ref.userId);
  if (direct) return direct;
  const nested = ref.user;
  if (nested && typeof nested === 'object') {
    const u = nested as Record<string, unknown>;
    const id = u._id ?? u.user_id ?? u.userId;
    if (id != null && String(id).trim()) return String(id).trim();
  }
  return '';
}

/** True when a recommendations[] row was an explicit user action (not auto-added). */
function isExplicitRecommendationRow(row: Record<string, unknown>): boolean {
  const src = row.recommendation_source ?? row.recommendationSource;
  // Backend contract: 'explicit' | 'enriched' on user-initiated rows; absent on implicit.
  return src === 'explicit' || src === 'enriched';
}

/**
 * Like `extractAvatarUrlsFromRecReferences` but returns `{ userId, avatarUrl }` pairs
 * so the UI can add/remove the current user's entry on recommend/unrecommend.
 *
 * @param explicitOnly - When true, skips implicit (auto-added) recommendations so the
 *   facepile count matches `rec_count_breakdown.explicit_recommendations`.
 */
export function extractRecommenderAvatarsWithIds(
  refs: unknown[] | undefined | null,
  explicitOnly = false,
): RecommenderAvatar[] {
  if (!Array.isArray(refs) || refs.length === 0) return [];
  const out: RecommenderAvatar[] = [];
  for (const ref of refs) {
    if (!ref || typeof ref !== 'object') continue;
    const row = ref as Record<string, unknown>;
    if (explicitOnly && !isExplicitRecommendationRow(row)) continue;
    const avatarUrl = avatarUrlFromRecReferenceRow(row);
    const userId = userIdFromRow(row);
    if (userId) out.push({ userId, avatarUrl });
  }
  return out;
}

/**
 * Paired version of `getRecommendationAvatarUrlsFromLocationLike` — returns userId+avatarUrl
 * from `rec_references` or `recommendations[]`.
 *
 * @param explicitOnly - Filter to explicit/enriched rows only (use when sourcing from
 *   GET /api/v1/location/:id which returns ALL recommendations including implicit ones).
 */
export function getRecommenderAvatarsWithIdsFromLocationLike(
  locationLike: unknown,
  explicitOnly = false,
): RecommenderAvatar[] {
  if (!locationLike || typeof locationLike !== 'object') return [];
  const obj = locationLike as Record<string, unknown>;

  // rec_references (featured aggregation) only contains explicit recommenders — no filter needed.
  const refs = getRecReferencesArrayFromLocationLike(obj);
  const fromRefs = extractRecommenderAvatarsWithIds(refs);
  if (fromRefs.length > 0) return fromRefs;

  const recs = obj.recommendations;
  if (!Array.isArray(recs) || recs.length === 0) return [];
  return extractRecommenderAvatarsWithIds(recs as unknown[], explicitOnly);
}

/**
 * Minimal location data interface for type safety
 */
interface LocationDataInput {
  _id?: string;
  location_id?: string;
  name?: string;
  description?: string; // Google editorial summary
  a3_intl_address?: string;
  address?: string;
  phone?: string;
  website?: string;
  photos?: Array<{ name?: string; photo_reference?: string }>;
  google_places_data?: {
    formatted_address?: string;
    user_ratings_total?: number;
    internationalPhoneNumber?: string;
    nationalPhoneNumber?: string;
    websiteUri?: string;
    types?: string[];
    type?: string[];
    photos?: Array<{ name?: string; photo_reference?: string }> | unknown[];
  };
  rec_count?: number;
  recCount?: number;
  recommendation_count?: number;
  recommendations?: unknown[];
  rec_count_breakdown?: RecCountBreakdown;
  breakdown?: RecCountBreakdown;
  rec_references?: RecReference[];
  /**
   * When opening CombinedContentPlaceModal from a list slot, set to the same
   * explicit count as convertTimelinePlaceToPlaceCardData (rec_references + post fallback).
   */
  list_slot_explicit_recommendations?: number;
  google_types?: string[];
  /** S3/HTTPS URLs and/or photo identifier objects (same as photoFormat=identifiers payloads) */
  image_urls?: unknown[];
}

/**
 * Utility functions for extracting place data for display in components.
 * This centralizes the data extraction logic to avoid duplication between
 * LocationModal, CombinedContentPlaceModal, and other components.
 */

/**
 * Extracted place data ready for display in PlaceDisplayCard
 */
export interface ExtractedLocationData {
  name: string;
  description?: string; // Google editorial summary
  address: string;
  image: string;
  phone: string | undefined;
  website: string | undefined;
  recommendations: number; // Total uses (rec_count)
  recommendationAvatars?: string[];
  explicitRecommendations: number; // Explicit recommendations from rec_references
  traveller_types: string[];
  locationId?: string;
}

/**
 * Extract photo URL from location data with multiple fallback strategies.
 * Handles both direct URLs (from social scraper) and Google Places photo references.
 * 
 * @param locationData - Location object with photos in various formats
 * @param maxWidth - Maximum width for Google Places photo URLs (default: 400)
 * @returns Photo URL string, or empty string if no photo found
 */
export function extractPhotoUrl(
  locationData: LocationDataInput | null | undefined,
  maxWidth: number = 400
): string {
  if (!locationData) return '';

  // Direct URL (e.g. Nuitee main_photo or images[0])
  const mainPhoto = (locationData as { main_photo?: string }).main_photo;
  if (typeof mainPhoto === 'string' && mainPhoto.trim()) return mainPhoto.trim();

  // S3 / user-uploaded image_urls first; fallback to Google photos if none or invalid
  if (locationData.image_urls && Array.isArray(locationData.image_urls)) {
    const first = locationData.image_urls.find(
      (url): url is string => typeof url === 'string' && url.length > 0 && !isDirectGooglePhotoUrl(url)
    );
    if (first) return first;
  }

  // Use unified photo utility for Google identifiers (extractPhotosFromLocation now puts image_urls first; we already tried those above)
  const photoUrl = getFirstPhotoUrl(
    {
      photos: locationData.photos as PhotoIdentifier[] | undefined,
      google_places_data: locationData.google_places_data ? {
        photos: (Array.isArray(locationData.google_places_data.photos) 
          ? locationData.google_places_data.photos as PhotoIdentifier[] 
          : undefined),
      } : undefined,
    },
    maxWidth
  );

  return photoUrl || '';
}

/**
 * Extract all place data needed for display in PlaceDisplayCard.
 * Centralizes the extraction logic with proper fallbacks.
 * 
 * @param locationData - Place/location object (from marker.location or place.locationData)
 * @param fallbackName - Optional fallback name (e.g., from marker.title or place.name)
 * @param fallbackAddress - Optional fallback address (e.g., from marker.address)
 * @returns Extracted place data ready for display
 */
export function extractLocationDataForDisplay(
  locationData: LocationDataInput | null | undefined,
  fallbackName?: string,
  fallbackAddress?: string
): ExtractedLocationData {
  // Extract address - check multiple possible locations
  const address = fallbackAddress ||
                 locationData?.a3_intl_address || 
                 locationData?.address || 
                 locationData?.google_places_data?.formatted_address || 
                 'Address not available';

  // Extract name
  const name = fallbackName ||
               locationData?.name || 
               'Unknown location';

  // Extract description (Google editorial summary)
  const description = locationData?.description || undefined;
  log('[extractLocationDataForDisplay] description', {
    locationName: fallbackName || locationData?.name || name,
    hasDescription: !!description,
    descriptionLength: description?.length ?? 0,
    descriptionPreview: description ? description.slice(0, 50) + (description.length > 50 ? '…' : '') : undefined,
  });

  // Extract photo URL
  const image = extractPhotoUrl(locationData);

  // Extract phone — root field first, then Google Places (New) on embedded google_places_data
  const gpdPhone = locationData?.google_places_data;
  const phoneFromGoogle =
    (typeof gpdPhone?.internationalPhoneNumber === 'string' && gpdPhone.internationalPhoneNumber.trim()) ||
    (typeof gpdPhone?.nationalPhoneNumber === 'string' && gpdPhone.nationalPhoneNumber.trim()) ||
    undefined;
  const phone = locationData?.phone || phoneFromGoogle || undefined;

  const websiteFromGoogle =
    typeof gpdPhone?.websiteUri === 'string' && gpdPhone.websiteUri.trim() ? gpdPhone.websiteUri.trim() : undefined;
  const website = locationData?.website || websiteFromGoogle || undefined;

  // Extract recommendations count
  // Priority: rec_count (most reliable, from API) > recCount (camelCase variant) > recommendations array length > fallback
  const rec_count = typeof locationData?.rec_count === 'number' ? locationData.rec_count : null;
  const recCount = typeof locationData?.recCount === 'number' ? locationData.recCount : null;
  const recommendationsArray = Array.isArray(locationData?.recommendations) ? locationData.recommendations : null;
  const recommendation_count = typeof locationData?.recommendation_count === 'number' ? locationData.recommendation_count : null;
  const user_ratings_total = typeof locationData?.google_places_data?.user_ratings_total === 'number' ? locationData.google_places_data.user_ratings_total : null;
  
  // rec_count_breakdown or breakdown provides the separated counts:
  // - post_locations: number of times location added to posts/lists/itineraries (Uses)
  // - explicit_recommendations: number of explicit "Rec it" clicks (Recs)
  // Backend may use either field name, so check both
  const recCountBreakdown = locationData?.rec_count_breakdown || locationData?.breakdown;
  const postLocationsCount = recCountBreakdown?.post_locations;
  
  // For "Uses" (recommendations prop), prefer rec_count_breakdown.post_locations
  // This is the accurate count of places in itineraries/lists (not including explicit recs)
  const recommendations = (typeof postLocationsCount === 'number' && postLocationsCount > 0)
    ? postLocationsCount
    : (rec_count !== null && rec_count > 0)
    ? rec_count
    : (recCount !== null && recCount > 0)
    ? recCount
    : (recommendationsArray !== null && recommendationsArray.length > 0)
    ? recommendationsArray.length
    : (recommendation_count !== null && recommendation_count > 0)
    ? recommendation_count
    : (user_ratings_total !== null && user_ratings_total > 0)
    ? user_ratings_total
    : 0;

  // Log extraction details at log level (recommendations + description summary)
  log('[extractLocationDataForDisplay] Extracted', {
    locationName: name,
    hasDescription: !!description,
    hasBreakdown: !!recCountBreakdown,
    finalRecommendations: recommendations,
  });

  // Explicit GT recs: list slot hint, else shared breakdown / rec_references helper.
  let explicitRecommendations = 0;
  let recommendationAvatars: string[] = [];

  const listSlotExplicit = locationData?.list_slot_explicit_recommendations;

  if (typeof listSlotExplicit === 'number' && listSlotExplicit >= 0) {
    explicitRecommendations = listSlotExplicit;
    recommendationAvatars = getRecommendationAvatarUrlsFromLocationLike(locationData);
  } else {
    explicitRecommendations = getExplicitRecBaseFromLocationData(locationData);
    recommendationAvatars = getRecommendationAvatarUrlsFromLocationLike(locationData);
  }
  // NOTE: explicit *count* must not use recommendations.length (mixed auto vs explicit recs).
  // Avatar URLs may still come from `recommendations[]` when `rec_references` has no image fields
  // — see `getRecommendationAvatarUrlsFromLocationLike`.

  // Extract traveller types (Google Places types)
  const traveller_types = locationData?.google_types || 
                         locationData?.google_places_data?.types || 
                         locationData?.google_places_data?.type || 
                         [];

  // Extract locationId for recommendation API calls
  const locationId = locationData?._id || locationData?.location_id || undefined;

  return {
    name,
    description,
    address,
    image,
    phone,
    website,
    recommendations,
    recommendationAvatars,
    explicitRecommendations,
    traveller_types,
    locationId,
  };
}

/**
 * Extract address from PostLocation and Location objects with proper fallback chain.
 * Used to ensure consistent address extraction across the codebase.
 * 
 * Priority order:
 * 1. postLocation.address (user-entered address for user-created places)
 * 2. location.a3_intl_address
 * 3. location.google_places_data.formattedAddress
 * 4. location.address (if string)
 * 5. Empty string
 * 
 * @param postLocationAddress - Address from PostLocation object
 * @param location - Location object with various address formats
 * @returns Extracted address string
 */
export function extractAddress(
  postLocationAddress: string | null | undefined,
  location?: {
    a3_intl_address?: string | null;
    google_places_data?: {
      formattedAddress?: string;
    } | null;
    address?: string | unknown;
  } | null
): string {
  return postLocationAddress || 
         location?.a3_intl_address || 
         location?.google_places_data?.formattedAddress || 
         (typeof location?.address === 'string' ? location.address : '') ||
         '';
}

/**
 * Strip "- Day X" suffix from body text.
 * Backend appends this suffix to PostLocation.body, so we need to remove it for display.
 * Example: "Elephant Room - Day 1" becomes "Elephant Room"
 * 
 * @param body - Body text that may contain day suffix
 * @returns Body text with day suffix removed
 */
export function stripDaySuffix(body: string | null | undefined): string {
  if (!body || typeof body !== 'string') return '';
  // Match pattern like " - Day 1", " - Day 2", etc. at the end
  return body.replace(/\s*-\s*Day\s+\d+\s*$/i, '').trim();
}

/**
 * Extract image URL from PostLocation and Location objects with proper fallback chain.
 * Used to ensure consistent image extraction across the codebase.
 * 
 * Priority order:
 * 1. postLocation.image_urls[0] (first image from PostLocation)
 * 2. location.photos[0] (identifier format: name or photo_reference)
 * 3. location.google_places_data.photos[0] (identifier format: name or photo_reference)
 * 4. Empty string
 * 
 * @param postLocationImageUrls - Image URLs from PostLocation (strings and/or identifier objects)
 * @param location - Location object with photos in various formats
 * @returns Extracted image URL string
 */
/**
 * Sanitize URL by removing whitespace, newlines, and control characters.
 * CSS backgroundImage and img src are very sensitive to whitespace in URLs.
 */
function sanitizeUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return url;
  }
  // Remove all whitespace, newlines, tabs, and control characters
  return url.trim().replace(/\s+/g, '').replace(/[\n\r\t]/g, '');
}

export function extractImageFromLocation(
  postLocationImageUrls: readonly unknown[] | null | undefined,
  location?: {
    photos?: Array<{ name?: string; photo_reference?: string }> | null;
    google_places_data?: {
      photos?: Array<{ name?: string; photo_reference?: string }> | null;
    } | null;
  } | null
): string {
  
  debug('[extractImageFromLocation] Starting extraction', {
    hasPostLocationImageUrls: !!(postLocationImageUrls && postLocationImageUrls.length > 0),
    postLocationImageUrlsCount: postLocationImageUrls?.length || 0,
    postLocationImageUrlsFirst: postLocationImageUrls?.[0] || null,
    hasLocation: !!location,
    locationKeys: location ? Object.keys(location) : [],
    hasLocationPhotos: !!(location?.photos && Array.isArray(location.photos)),
    locationPhotosCount: location?.photos?.length || 0,
    hasGooglePlacesData: !!location?.google_places_data,
    hasGooglePlacesPhotos: !!(location?.google_places_data?.photos && Array.isArray(location.google_places_data.photos)),
    googlePlacesPhotosCount: location?.google_places_data?.photos?.length || 0,
  });

  // Priority 1: Use postLocationImageUrls if the first entry is a non-empty URL string
  // (arrays may hold photo identifier objects — fall through to getFirstPhotoUrl for those)
  if (postLocationImageUrls && postLocationImageUrls.length > 0) {
    const originalResult = postLocationImageUrls[0];
    if (typeof originalResult !== 'string' || !originalResult.trim()) {
      debug('[extractImageFromLocation] Skipping non-string image_urls[0], using location fallbacks', {
        typeOfFirst: typeof originalResult,
      });
    } else {
    const result = sanitizeUrl(originalResult);
    const wasSanitized = originalResult !== result;
    
    debug('[extractImageFromLocation] Using postLocationImageUrls[0]', {
      url: result,
      originalUrl: wasSanitized ? originalResult : undefined,
      urlLength: result.length,
      originalLength: originalResult.length,
      wasSanitized,
      source: 'postLocationImageUrls',
    });
    
    if (wasSanitized) {
      warn('[extractImageFromLocation] URL was sanitized (removed whitespace/newlines)', {
        originalUrl: originalResult,
        sanitizedUrl: result,
      });
    }
    
    return result;
    }
  }
  
  // Priority 2: Use unified photo utility to extract from location data (handles identifier format)
  if (location) {
    // Filter out null from photos array
    const locationData = {
      photos: location.photos ? (Array.isArray(location.photos) ? location.photos.filter((p): p is { name?: string; photo_reference?: string } => p !== null) : undefined) : undefined,
      google_places_data: location.google_places_data ? {
        photos: location.google_places_data.photos ? (Array.isArray(location.google_places_data.photos) ? location.google_places_data.photos.filter((p): p is { name?: string; photo_reference?: string } => p !== null) : undefined) : undefined,
      } : undefined,
    };
    
    debug('[extractImageFromLocation] Calling getFirstPhotoUrl with locationData', {
      locationDataHasPhotos: !!(locationData.photos && Array.isArray(locationData.photos)),
      locationDataPhotosCount: locationData.photos?.length || 0,
      locationDataHasGooglePlacesData: !!locationData.google_places_data,
      locationDataHasGooglePlacesPhotos: !!(locationData.google_places_data?.photos && Array.isArray(locationData.google_places_data.photos)),
      locationDataGooglePlacesPhotosCount: locationData.google_places_data?.photos?.length || 0,
      samplePhoto: locationData.photos?.[0] ? JSON.stringify(locationData.photos[0]) : 'none',
      sampleGooglePlacesPhoto: locationData.google_places_data?.photos?.[0] ? JSON.stringify(locationData.google_places_data.photos[0]) : 'none',
    });
    
    const photoUrl = getFirstPhotoUrl(locationData, 400);
    const sanitizedPhotoUrl = sanitizeUrl(photoUrl);
    const wasSanitized = photoUrl !== sanitizedPhotoUrl;
    
    if (sanitizedPhotoUrl) {
      debug('[extractImageFromLocation] Successfully extracted from location', {
        url: sanitizedPhotoUrl,
        originalUrl: wasSanitized ? photoUrl : undefined,
        urlLength: sanitizedPhotoUrl.length,
        originalLength: photoUrl.length,
        wasSanitized,
        source: 'location.photos or location.google_places_data.photos',
      });
      
      if (wasSanitized) {
        warn('[extractImageFromLocation] URL was sanitized (removed whitespace/newlines)', {
          originalUrl: photoUrl,
          sanitizedUrl: sanitizedPhotoUrl,
        });
      }
      
      return sanitizedPhotoUrl;
    } else {
      debug('[extractImageFromLocation] getFirstPhotoUrl returned empty string', {
        locationDataKeys: Object.keys(locationData),
        locationDataPhotos: locationData.photos ? JSON.stringify(locationData.photos.slice(0, 2)) : 'none',
        locationDataGooglePlacesPhotos: locationData.google_places_data?.photos ? JSON.stringify(locationData.google_places_data.photos.slice(0, 2)) : 'none',
      });
    }
  } else {
    debug('[extractImageFromLocation] No location provided, returning empty string');
  }
  
  debug('[extractImageFromLocation] Returning empty string - no image found', {
    hadPostLocationImageUrls: !!(postLocationImageUrls && postLocationImageUrls.length > 0),
    hadLocation: !!location,
  });
  
  return '';
}

